import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import HubspotLogo from '../../public/HubspotLogo.png';
import MailchimpLogo from '../../public/MailchimpLogo.png';
import ChatGptLogo from '../../public/ChatGptLogo.png';
import chartWorkflow from '../../public/ChartWorkflow.png';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Slideout, Collapse, Tag, Button, Avatar, Typography } from '@ann/web-components';
import { DragIndicator } from '@mui/icons-material';
import { CollapseContainer } from '../../pages/TriggersCreatedPage/TriggersCreatedPage.styled';
import { 
  ButtonWrapper,
  ScrollableContainer,
  HighlightsContainer,
  MetricContainer,
  MetricContent,
  WorkflowImageContainer,
  AvatarContainer,
  OverviewContainer,
  OverviewInfoRow,
  OverviewInfoColumn,
  WorkflowContainer,
  WorkflowSection,
  TagWrapper
} from './PreviewSlideOut.styles';


const SortableCollapseItem = ({ id, title, children, defaultOpen = false }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    marginBottom: '8px',
  };

  return (  
    <div ref={setNodeRef} style={style}>
      <Collapse
        title={
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <div
              {...attributes}
              {...listeners}
              style={{
                cursor: 'grab',
                padding: '4px',
                marginRight: '8px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
            >
              <DragIndicator 
                style={{ 
                  fontSize: '16px',
                  color: '#666',
                }} 
              />
            </div>
            <span style={{ flex: 1 }}>{title}</span>
          </div>
        }
        defaultOpen={defaultOpen}
        onClose={() => {}}
      >
        {children}
      </Collapse>
    </div>
  );
};

const PreviewSlideOut = ({ open, onClose, trigger }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([
    { 
      id: '1', 
      title: 'Overview',
      defaultOpen: true,
      content: 'overview' 
    },
    { 
      id: '2', 
      title: 'Workflow', 
      defaultOpen: true,
      content: 'workflow' 
    },
    { 
      id: '3', 
      title: 'Highlights', 
      defaultOpen: true,
      content: 'highlights' 
    },
  ]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const getStatusConfig = (status) => {
  const statusMap = {
    not_set_up_yet: { type: "neutral", label: "Not set up yet" },
    active: { type: "success", label: "Active" },
    paused: { type: "alert", label: "Paused" },
    scheduled: { type: "informative", label: "Scheduled" },
    auto_deactivated: { type: "neutral", label: "Auto deactivated" },
    disabled: { type: "neutral", label: "Disabled" },
    archived: { type: "neutral", label: "Archived" },
    disabled_system: { type: "error", label: "Disabled (System)" },
  };

  return statusMap[status] || { type: "neutral", label: "Unknown" };
};

  const renderContent = (contentType) => {
    if (!trigger) return null;
    
    switch (contentType) {
      case 'overview':
        return (
          <OverviewContainer>
            <OverviewContainer>
              <div>
                <b>Description:</b> 
              </div>
              <div>
                {trigger.description.props?.text || trigger.description}
              </div>
            </OverviewContainer>
            <OverviewInfoRow>
              <OverviewInfoColumn>
                <div><b>Type:</b></div>
                <div>
                  {trigger.type}
                </div>
              </OverviewInfoColumn>
              <OverviewInfoColumn>
                <div><b>Source:</b></div>
                <div>
                  {trigger.source}
                </div>
              </OverviewInfoColumn>
              <OverviewInfoColumn>
                <div><b>Last updated:</b></div>
                <div>
                  {trigger.lastUpdated}
                </div>
              </OverviewInfoColumn>
              <OverviewInfoColumn>
                <div><b>Status:</b></div>
                <div>
                  <Tag
                    text={trigger.status.props?.text || getStatusConfig(trigger.status).label}
                    color={trigger.status.props?.color || getStatusConfig(trigger.status).type}
                    size='medium'
                    icon={trigger.status.props?.icon || getStatusConfig(trigger.status).icon}
                  />
                </div>
              </OverviewInfoColumn>
              <OverviewInfoColumn>
                <div><b>Created by:</b></div>
                <div>
                  {trigger.createdBy}
                </div>
              </OverviewInfoColumn>
            </OverviewInfoRow>
          </OverviewContainer>
        );
      case 'workflow':
        return (
          <WorkflowContainer>
            <WorkflowSection>
              <div>
                <b>Summary:</b> 
              </div>
              <div>
                Sends a welcome message via Slack and follow-up emails/SMS through connected channels like Gmail and Mailchimp. Reminders are triggered if there's no reply within 48 hours.
              </div>
            </WorkflowSection>
            <WorkflowSection>
              <div>
                <b>Used Nodes:</b> 
              </div>
              <AvatarContainer>
                <div>
                  <Avatar 
                    size='md'
                    src={HubspotLogo}
                  />
                  <Avatar 
                    size='md'
                    src={MailchimpLogo}
                  />
                  <Avatar 
                    size='md'
                    src={ChatGptLogo}
                    />
                </div>
                <p> +34 </p>
              </AvatarContainer>

              <div>
                <WorkflowImageContainer>
                  <img src={chartWorkflow} alt='Workflow' style={{ width: '60%', height: 'auto' }} />
                  <ButtonWrapper>
                    <Button
                      iconRight='openInFull'                  
                      type='secondary'
                      size='small'
                      onClick={() => {}}
                    />
                  </ButtonWrapper>
                </WorkflowImageContainer>
              </div>
            </WorkflowSection>
          </WorkflowContainer>
        );
      case 'highlights':
        return (
          <HighlightsContainer>
            <MetricContainer>
              <MetricContent>
                <Typography
                  category='title'
                  size='medium'
                  weight='bold'
                >
                  executions
                </Typography>
                <Typography
                  category='title'
                  size='large'
                  weight='bold'
                >
                  15823
                </Typography>
              </MetricContent>
              <TagWrapper>
                <Tag
                  icon='trendingUp'
                  color='success'
                  size='medium'                  
                />
              </TagWrapper>
            </MetricContainer>
            <MetricContainer>
              <MetricContent>
                <Typography
                  category='title'
                  size='medium'
                  weight='bold'
                >
                  Success Rate
                </Typography>
                <Typography
                  category='title'
                  size='large'
                  weight='bold'
                >
                  42.3 %
                </Typography>
              </MetricContent>
              <TagWrapper>
                <Tag
                  icon='doneAll'
                  color='success'
                  size='medium'                  
                />
              </TagWrapper>
            </MetricContainer>
          </HighlightsContainer>
        );
      default:
        return null;
    }
  };

  if (!trigger) return null;
  
  return (
    <Slideout
      title={trigger.name}
      open={open}
      onClose={onClose}
    >
      <Slideout.Title size="large">{trigger.name}</Slideout.Title>
      <Slideout.Content>
        <ScrollableContainer>
          <CollapseContainer>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={items.map(item => item.id)}
                strategy={verticalListSortingStrategy}
              >
                {items.map((item) => (
                  <SortableCollapseItem
                    key={item.id}
                    id={item.id}
                    title={item.title}
                    defaultOpen={item.defaultOpen}
                  >
                    {renderContent(item.content)}
                  </SortableCollapseItem>
                ))}
              </SortableContext>
            </DndContext>
          </CollapseContainer>
        </ScrollableContainer>
      </Slideout.Content>
       <Slideout.Actions>
            <Button label={'Go to workflow'} type='primary' size={'medium'} onClick={() => navigate(`/v2/workflows/${trigger.uuid}`)} />
        </Slideout.Actions>
    </Slideout>
  );
};

export default PreviewSlideOut; 